import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Checkbox, Chip, IconButton, InputAdornment,
    Paper, Stack, TableBody, TableCell, TableHead, TableRow,
    TextField, Typography,
} from "@mui/material";
import FormModal from "../../../../component/common/FormModal";
import ResizableTable from "../../../../component/common/ResizableTable";
import TableToolbar from "../../../../component/common/TableToolbar";
import { useTableSearch } from "../../../../util/tableSearch";
import ActionButton from "../../../../component/common/ActionButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import { financeApi, fmtComma, fmtKRWFull } from "../../api/financeApi";
import { ds } from "../../../../styles/designSystem";
import { formatDate, today as getToday } from "../../../../util/dateUtils";
import { formatNumberWithCommas, toNumericInputState } from "../../../../util/numberFormat";
import FormField from "../../../../component/common/FormField";
import LoadingDots from "../../../../component/common/LoadingDots";
import ComboBox from "../../../../component/common/ComboBox";
import VendorAutocomplete from "../../components/vendor/VendorAutocomplete";
import { VendorCell, ProjectCell } from "../../components/FinanceLinkProvider";
import { useTableSort } from "../../../../util/useTableSort";
import { confirm as confirmDialog } from "../../../../util/confirmDialog";

const STATUS_OPTIONS = [
    { value: "OUTSTANDING", label: "미수금", color: "#dc2626", bg: "#fef2f2" },
    { value: "PARTIALLY_PAID", label: "일부수금", color: "#d97706", bg: "#fffbeb" },
    { value: "PAID", label: "수금완료", color: "#16a34a", bg: "#f0fdf4" },
];
const statusInfo = (s) => STATUS_OPTIONS.find(o => o.value === s) || STATUS_OPTIONS[0];

// 수금 합계로 상태를 파생 계산 (서버와 동일 규칙) — 저장 전 미리보기용
const deriveStatus = (billing, collected) =>
    collected <= 0 ? "OUTSTANDING" : collected >= billing ? "PAID" : "PARTIALLY_PAID";

const EMPTY = {
    vendorId: null, clientName: "", projectName: "",
    billingAmount: "", dueDate: formatDate(getToday()), note: "",
};

let _pkSeq = 1;
const newPayment = (todayStr) => ({
    _key: `new-${_pkSeq++}`, id: null, amount: "", amountDisplay: "",
    paidDate: todayStr, bankAccountId: "", note: "",
});

export default function ReceivableTab({ projectChannels = [] }) {
    const todayStr = formatDate(getToday());
    const [rows, setRows] = useState([]);
    // 표 안 검색 — 공통 유틸이 행 전체를 훑는다.
    const [tableQuery, setTableQuery] = useState("");
    const rowsFiltered = useTableSearch(rows, tableQuery);

    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [billingDisplay, setBillingDisplay] = useState("");
    const [payments, setPayments] = useState([]);   // 수금(결제)건 목록
    const [selected, setSelected] = useState(() => new Set());

    const { toggleSort, sortLabel, sortRows } = useTableSort("dueDate", "desc");

    const load = () => {
        setLoading(true);
        return financeApi.getReceivables()
            .then(setRows)
            .catch(() => {})
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);
    useEffect(() => { financeApi.getBankAccounts().then(d => setAccounts(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

    // KPI — 미수금액은 파생값, 수금완료는 Σ수금완료액(부분수금 포함)
    const totalReceivable = rows.filter(r => r.status !== "PAID").reduce((s, r) => s + (r.receivableAmount || 0), 0);
    const totalBilling = rows.reduce((s, r) => s + (r.billingAmount || 0), 0);
    const collectedTotal = rows.reduce((s, r) => s + (r.collectedAmount || 0), 0);

    const sortedRows = useMemo(() => sortRows(rowsFiltered), [rowsFiltered, sortRows]);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setBillingDisplay("");
        setPayments([]);
        setOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            vendorId: row.vendorId ?? null, clientName: row.clientName || "",
            projectName: row.projectName || "", billingAmount: row.billingAmount ?? "",
            dueDate: row.dueDate || todayStr, note: row.note || "",
        });
        setBillingDisplay(formatNumberWithCommas(row.billingAmount, { emptyZero: true }));
        setPayments((row.payments || []).map(p => ({
            _key: `p-${p.id}`, id: p.id, amount: p.amount, amountDisplay: formatNumberWithCommas(p.amount, { emptyZero: true }),
            paidDate: p.paidDate || todayStr, bankAccountId: p.bankAccountId || "", note: p.note || "",
        })));
        setOpen(true);
    };

    const handleBillingInput = (raw) => {
        const { value, display } = toNumericInputState(raw, { emptyZero: true });
        setForm(f => ({ ...f, billingAmount: value }));
        setBillingDisplay(display);
    };

    const handleChannelSelect = (channelId) => {
        const ch = projectChannels.find(c => String(c.id) === String(channelId));
        setForm(f => ({ ...f, projectName: ch ? ch.name : "" }));
    };

    // ─── 수금건 편집 ───
    const addPayment = () => setPayments(ps => [...ps, newPayment(todayStr)]);
    const removePayment = (key) => setPayments(ps => ps.filter(p => p._key !== key));
    const patchPayment = (key, patch) => setPayments(ps => ps.map(p => p._key === key ? { ...p, ...patch } : p));
    const patchPaymentAmount = (key, raw) => {
        const { value, display } = toNumericInputState(raw, { emptyZero: true });
        patchPayment(key, { amount: value, amountDisplay: display });
    };

    // 저장 전 파생 미리보기
    const billingNum = Number(form.billingAmount) || 0;
    const collectedNum = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const remainingNum = Math.max(0, billingNum - collectedNum);
    const previewStatus = deriveStatus(billingNum, collectedNum);
    const overCollected = collectedNum > billingNum;

    const handleSave = async () => {
        const payload = {
            vendorId: form.vendorId,
            clientName: form.clientName,
            projectName: form.projectName,
            billingAmount: Number(form.billingAmount),
            dueDate: form.dueDate,
            note: form.note,
            payments: payments
                .filter(p => Number(p.amount) > 0)
                .map(p => ({
                    id: p.id ?? null,
                    amount: Number(p.amount),
                    paidDate: p.paidDate || todayStr,
                    bankAccountId: p.bankAccountId || null,
                    note: p.note || null,
                })),
        };
        if (editing) await financeApi.updateReceivable(editing.id, payload);
        else await financeApi.createReceivable(payload);
        setOpen(false);
        load();
    };

    // 일괄 삭제
    const toggleSelect = (id) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const toggleSelectAll = (list) => {
        const targets = list.map(r => r.id);
        setSelected(prev => targets.length > 0 && targets.every(id => prev.has(id)) ? new Set() : new Set(targets));
    };
    const handleBulkDelete = async () => {
        if (selected.size === 0) return;
        if (!(await confirmDialog({ message: `선택한 ${selected.size}건을 삭제하시겠습니까? (수금 실적도 함께 제거됩니다)` }))) return;
        await Promise.allSettled([...selected].map(id => financeApi.deleteReceivable(id)));
        setSelected(new Set());
        load();
    };

    return (
        <Box>
            {/* KPI */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>미수금 총액</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#d97706", mt: 0.5 }}>
                        {fmtKRWFull(totalReceivable)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {rows.filter(r => r.status !== "PAID").length}건
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>청구 금액 합계</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#2563eb", mt: 0.5 }}>
                        {fmtKRWFull(totalBilling)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{rows.length}건</Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>수금 완료</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#16a34a", mt: 0.5 }}>
                        {fmtKRWFull(collectedTotal)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>부분수금 포함</Typography>
                </Paper>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                {selected.size > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 0.8, bgcolor: "#fef2f2", borderRadius: 1.5 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#dc2626" }}>{selected.size}건 선택됨</Typography>
                        {/* 검색 — 동작 버튼 왼쪽, 우측 상단 고정(전 시스템 공통 배치) */}
                        <TableToolbar onSearch={setTableQuery} placeholder="거래처·내용 검색"
                            sx={{ flex: 1, mb: 0, minWidth: 200 }} />
                        <ActionButton variant="secondary" onClick={handleBulkDelete}
                            startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                            label="선택 삭제" sx={{ fontSize: 12, color: "#dc2626" }} />
                        <Chip label="선택 해제" size="small" onClick={() => setSelected(new Set())} sx={{ height: 22, fontSize: 11, cursor: "pointer" }} />
                    </Box>
                )}
                <Box sx={{ flex: 1 }} />
                <ActionButton variant="add" onClick={openCreate} label="미수금 등록" />
            </Box>

            <Paper sx={ds.card}>
                {loading && <LoadingDots />}
                {!loading && <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox size="small"
                                    checked={sortedRows.length > 0 && sortedRows.every(r => selected.has(r.id))}
                                    indeterminate={selected.size > 0 && !sortedRows.every(r => selected.has(r.id))}
                                    onChange={() => toggleSelectAll(sortedRows)} />
                            </TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("clientName")}>{sortLabel("clientName", "거래처")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("projectName")}>{sortLabel("projectName", "프로젝트")}</TableCell>
                            <TableCell align="right" sx={{ cursor: "pointer" }} onClick={() => toggleSort("billingAmount")}>{sortLabel("billingAmount", "청구금액")}</TableCell>
                            <TableCell align="right" sx={{ cursor: "pointer" }} onClick={() => toggleSort("receivableAmount")}>{sortLabel("receivableAmount", "미수금액")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("dueDate")}>{sortLabel("dueDate", "청구일")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("dDay")}>{sortLabel("dDay", "D-day")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>{sortLabel("status", "상태")}</TableCell>
                            <TableCell>수금완료일</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4, color: "#94a3b8" }}>등록된 미수금이 없습니다.</TableCell>
                            </TableRow>
                        ) : sortedRows.map(row => {
                            const si = statusInfo(row.status);
                            const dd = row.dDay ?? null;
                            const dDayLabel = dd == null ? "—" : dd > 0 ? `D+${dd}` : dd === 0 ? "D-0" : `D${dd}`;
                            const collected = row.collectedAmount ?? Math.max(0, (row.billingAmount || 0) - (row.receivableAmount || 0));
                            return (
                                <TableRow key={row.id} hover onClick={() => openEdit(row)} sx={{ cursor: "pointer" }}>
                                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                        <Checkbox size="small" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} />
                                    </TableCell>
                                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                                        <VendorCell vendorId={row.vendorId} vendorName={row.clientName} />
                                    </TableCell>
                                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                                        <ProjectCell channelId={row.channelId} channelName={row.projectName} />
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                        +{fmtKRWFull(row.billingAmount)}
                                        {collected > 0 && (
                                            <Box sx={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", mt: 0.2 }}>
                                                {/* (수금 / 청구) */}
                                                (수금 {fmtComma(collected)})
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: "#d97706", whiteSpace: "nowrap" }}>
                                        {fmtKRWFull(row.receivableAmount)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{row.dueDate}</TableCell>
                                    <TableCell align="center">
                                        <Chip label={dDayLabel} size="small" sx={{
                                            bgcolor: dd > 0 ? "#fef2f2" : "#f0fdf4", color: dd > 0 ? "#dc2626" : "#16a34a", fontWeight: 700,
                                        }} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label={si.label} size="small" sx={{ bgcolor: si.bg, color: si.color, fontWeight: 600 }} />
                                    </TableCell>
                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", color: "#16a34a", fontSize: 12 }}>{row.paidAt ?? "—"}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ResizableTable>}
            </Paper>

            {/* 등록/수정 다이얼로그 */}
            <FormModal
                open={open}
                onClose={() => setOpen(false)}
                title={editing ? "미수금 수정" : "미수금 등록"}
                maxWidth="sm"
                onSave={handleSave}
                saveDisabled={!form.clientName || !form.billingAmount || overCollected}
            >
                <Stack spacing={2}>
                    <FormField label="거래처" required>
                        <VendorAutocomplete
                            freeSolo value={form.vendorId} initialName={form.clientName}
                            onChange={(id, vendorOrText) => {
                                if (typeof vendorOrText === "string") setForm(f => ({ ...f, vendorId: null, clientName: vendorOrText }));
                                else if (vendorOrText) setForm(f => ({ ...f, vendorId: id, clientName: vendorOrText.name }));
                                else setForm(f => ({ ...f, vendorId: null, clientName: "" }));
                            }}
                            label={null} helperText="등록된 거래처에서 선택하거나 그대로 입력하세요" />
                    </FormField>

                    <FormField label="프로젝트 채널">
                        <ComboBox clearable={false} placeholder="직접 입력"
                            options={[{ value: "", label: "직접 입력" }, ...projectChannels.map(ch => ({ value: ch.id, label: ch.name }))]}
                            value={projectChannels.find(c => c.name === form.projectName)?.id || ""}
                            onChange={v => handleChannelSelect(v)}
                            renderOption={(props, o) => (
                                <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {o.value !== "" && <FolderSpecialRoundedIcon sx={{ fontSize: 14, color: "#2563eb" }} />}
                                    {o.label}
                                </Box>
                            )} />
                    </FormField>

                    <FormField label="프로젝트명">
                        <TextField size="small" value={form.projectName}
                            onChange={e => setForm(f => ({ ...f, projectName: e.target.value }))}
                            placeholder="프로젝트명을 입력하세요" />
                    </FormField>

                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <FormField label="청구금액" required sx={{ flex: 1, minWidth: 0 }}>
                            <TextField fullWidth size="small" value={billingDisplay}
                                onChange={e => handleBillingInput(e.target.value)}
                                InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                                inputProps={{ inputMode: "numeric" }} placeholder="0" />
                        </FormField>
                        <FormField label="청구일" required sx={{ flex: 1, minWidth: 0 }}>
                            <TextField fullWidth size="small" type="date" value={form.dueDate}
                                onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
                        </FormField>
                    </Box>

                    {/* ── 수금 내역 — 등록하면 그만큼 확정 실적으로, 남은 건 예정으로 현금흐름표에 반영 ── */}
                    <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, p: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#334155" }}>수금 내역</Typography>
                            <Box sx={{ flex: 1 }} />
                            <ActionButton variant="secondary" onClick={addPayment}
                                startIcon={<AddIcon sx={{ fontSize: 14 }} />} label="수금 추가" sx={{ fontSize: 12 }} />
                        </Box>

                        {payments.length === 0 ? (
                            <Typography sx={{ fontSize: 12, color: "#94a3b8", py: 0.5 }}>
                                아직 수금 내역이 없습니다. 일부/전액 수금 시 '수금 추가'로 등록하세요.
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {payments.map(p => (
                                    <Box key={p._key} sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
                                        <TextField size="small" placeholder="수금액" value={p.amountDisplay}
                                            onChange={e => patchPaymentAmount(p._key, e.target.value)}
                                            InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                                            inputProps={{ inputMode: "numeric" }} sx={{ width: 150 }} />
                                        <TextField size="small" type="date" value={p.paidDate}
                                            onChange={e => patchPayment(p._key, { paidDate: e.target.value })} sx={{ width: 150 }} />
                                        <Box sx={{ width: 150 }}>
                                            <ComboBox placeholder="계좌(선택)"
                                                options={[{ value: "", label: "계좌 없음" }, ...accounts.map(a => ({ value: a.id, label: a.alias || a.bankName }))]}
                                                value={p.bankAccountId}
                                                onChange={v => patchPayment(p._key, { bankAccountId: v })} />
                                        </Box>
                                        <TextField size="small" placeholder="비고" value={p.note}
                                            onChange={e => patchPayment(p._key, { note: e.target.value })} sx={{ flex: 1, minWidth: 100 }} />
                                        <IconButton size="small" onClick={() => removePayment(p._key)}>
                                            <DeleteIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        )}

                        {/* 요약 — 청구/수금/미수/상태 자동 계산 */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.25, pt: 1, borderTop: "1px dashed #e2e8f0", flexWrap: "wrap" }}>
                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                청구 <b>{fmtComma(billingNum)}</b> · 수금 <b style={{ color: "#16a34a" }}>{fmtComma(collectedNum)}</b>
                                {" · "}미수 <b style={{ color: "#d97706" }}>{fmtComma(remainingNum)}</b>
                            </Typography>
                            <Chip label={statusInfo(previewStatus).label} size="small"
                                sx={{ bgcolor: statusInfo(previewStatus).bg, color: statusInfo(previewStatus).color, fontWeight: 700, height: 20, fontSize: 11 }} />
                            {overCollected && (
                                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#dc2626" }}>
                                    수금 합계가 청구금액을 초과했습니다
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <FormField label="비고">
                        <TextField size="small" multiline rows={2} value={form.note}
                            onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                            placeholder="비고를 입력하세요" />
                    </FormField>
                </Stack>
            </FormModal>
        </Box>
    );
}
